import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Switch, Tooltip, TableCellProps } from '@mui/material';
import CustomTablePagination from '../../../shared/CustomTablePagination';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { AlertCircle } from '@jda/lui-common-icon-library-mui5';
import CloseIcon from '@mui/icons-material/Close';
import {
  getAllIdps,
  getPaginationDetails,
  selectAllFilteredUsers,
  selectSearch,
  selectSearchKey
} from '../../../store/user/userSelector';
import { getUsers } from '../../../store/user/users';
import useWindowDimensions from '../../../utility/useWindowDimensions';
import { IUser } from '../../../models/Users/UserContracts';
import { LABEL } from '../../../components/UserTable/constants';
import {
  ADD_USERS,
  SHOW_SELECTED_USERS,
  RESET,
  SAVE,
  ROWS_PER_PAGE_LABEL,
  ADD_USER_TO_GROUP_ERROR,
  MAX_SELECTED_USERS_WARNING,
  MAX_REQUEST_ITEMS,
  USER_SAVED_SUCCESS_MESSAGE
} from '../constants';
import { useStyles } from './styles';
import { addUsersToGroup } from '../../../services/userGroups';
import { setSnackBarMessage } from '../../../store/snackBar/snackBarActionCreator';
import { fetchUsersByGroup } from '../../../store/userGroups/usersByGroup';
import { selectPaginationDetails } from '../../../store/userGroups/userGroupSelector';
import SnackbarAlert from '../../../shared/SnackbarAlert';
import { selectSnackBarSuccess } from '../../../store/snackBar/snackBarSelector';
import CustomSearch from '../../../components/CustomSearch';
import { DROPDOWN_MAX_HEIGHT, FOR, SEARCH_FILTER_KEYS, SEARCH_USER } from '../../Users/constants';
import { resetUsersState, setSearchKey, setSearchUser } from '../../../store/user/userActionCreator';
import {
  LuiDivider,
  LuiMaterialTableHeadCellStyles,
  LuiPageHeader,
  LuiSelect,
  LuiSelectItemEvent,
  LuiToolbar
} from '@jda/lui-common-component-library-mui5';
import SearchIcon from '@mui/icons-material/Search';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import {
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';

interface FullScreenDialogProps {
  open: boolean;
  identifier: string;
  withTrashIcon?: boolean;
  handleClose: () => void;
}

export default function FullScreenDialog({ open, identifier, handleClose }: FullScreenDialogProps) {
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const [selectedUsersToShow, setSelectedUsersToShow] = useState<IUser[]>([]);
  const [isSelectedUsersShown, setIsSelectedUsersShown] = useState<boolean>(false);
  const [isToggleSearch, setIsToggleSearch] = useState<boolean>(false);
  const [pageDetailsSelectedUsers, setPageDetailsSelectedUsers] = useState<any>({
    pageNumber: 1,
    pageSize: 50
  });
  const selectGetAllIdps = useSelector(getAllIdps);
  const filteredUsers = useSelector(selectAllFilteredUsers);
  const selectSearchValue = useSelector(selectSearch);
  const selectedPagination = useSelector(getPaginationDetails);
  const searchKey = useSelector(selectSearchKey);
  const pageDetailsUserByGroup = useSelector(selectPaginationDetails);
  const snackBarMessage = useSelector(selectSnackBarSuccess);
  const { height } = useWindowDimensions();
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const usersToShow = isSelectedUsersShown ? selectedUsersToShow : filteredUsers;
  const paginationCount = isSelectedUsersShown ? selectedUsers.length : selectedPagination.count;

  const idpMap = useMemo(() => new Map<string, any>(selectGetAllIdps.map((idp: any) => [idp.id, idp])), [
    selectGetAllIdps
  ]);

  useEffect(() => {
    if (selectedUsers.length === 0 && isSelectedUsersShown) {
      setIsSelectedUsersShown(false);
    }
  }, [selectedUsers, isSelectedUsersShown]);

  const filterParams = useMemo(
    () =>
      selectSearchValue
        ? {
            'filter-by-key': searchKey?.key,
            'filter-by-value': selectSearchValue
          }
        : undefined,
    [selectSearchValue, searchKey?.key]
  );

  const getAllUsers = useCallback(() => {
    const search = {
      ...filterParams
    };
    getUsers(dispatch, search);
  }, [dispatch, filterParams]);

  useEffect(() => {
    getAllUsers();
    return () => {
      dispatch(resetUsersState());
    };
  }, [dispatch, getAllUsers]);

  const getPaginatedSelectedUsers = (pageNumber: number, pageSize: number) => {
    return selectedUsers.slice((pageNumber - 1) * pageSize, (pageNumber - 1) * pageSize + pageSize);
  };

  const updateSelectedUsersToShow = (pageNumber: number, pageSize: number) => {
    const users = getPaginatedSelectedUsers(pageNumber, pageSize);
    setSelectedUsersToShow(users);
  };

  useEffect(() => {
    if (isSelectedUsersShown) {
      updateSelectedUsersToShow(pageDetailsSelectedUsers.pageNumber, pageDetailsSelectedUsers.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectedUsersShown, selectedUsers, pageDetailsSelectedUsers.pageNumber, pageDetailsSelectedUsers.pageSize]);

  const handleChangePageOptions = (itemsPerPage: number, page: number) => {
    const search = {
      'page-number': page,
      'page-size': itemsPerPage,
      ...filterParams
    };

    if (isSelectedUsersShown) {
      setPageDetailsSelectedUsers({
        ...pageDetailsSelectedUsers,
        pageNumber: page,
        pageSize: itemsPerPage
      });

      updateSelectedUsersToShow(page, itemsPerPage);
    } else {
      getUsers(dispatch, search);
    }
  };

  const columns = [
    {
      header: LABEL.USER_NAME,
      accessorKey: 'userName',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '25%'
          })
        } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.userName}
          placement="bottom"
          disableHoverListener={!row.original.userName}
          disableFocusListener={!row.original.userName}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.userName}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.NAME,
      accessorKey: 'displayName',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '25%'
          })
        } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.displayName || LABEL.NO_VALUE}
          placement="bottom"
          disableHoverListener={!row.original.displayName}
          disableFocusListener={!row.original.displayName}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.displayName || LABEL.NO_VALUE}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.EMAIL,
      accessorKey: 'email',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '25%'
          })
        } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
        <Tooltip
          arrow
          title={row.original.email || LABEL.NO_VALUE}
          placement="bottom"
          disableHoverListener={!row.original.email}
          disableFocusListener={!row.original.email}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.email || LABEL.NO_VALUE}
          </Typography>
        </Tooltip>
      )
    },
    {
      header: LABEL.IDP_PROVIDER,
      accessorKey: 'idpId',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '25%'
          })
        } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
        const idp = idpMap.get(row.original.idpId);
        const idpName = idp?.name ?? LABEL.NO_VALUE;
        return (
          <Tooltip arrow title={idpName} placement="bottom" disableHoverListener={!idp} disableFocusListener={!idp}>
            <Typography variant="body1" color="textPrimary" noWrap>
              {idpName}
            </Typography>
          </Tooltip>
        );
      }
    }
  ].flat();

  const handleSelectedUsersShown = (event: React.ChangeEvent<HTMLInputElement>) =>
    setIsSelectedUsersShown(event.target.checked);

  const handleResetSelectedUsers = () => setSelectedUsers([]);

  const handleSaveSelectedUsers = async () => {
    const checkedUsersIds = selectedUsers.map((item: any) => item.id);
    try {
      await addUsersToGroup(identifier, checkedUsersIds);
      handleResetSelectedUsers();
      const search = {
        'page-number': pageDetailsUserByGroup.pageNumber,
        'page-size': pageDetailsUserByGroup.pageSize
      };
      fetchUsersByGroup(dispatch, identifier, search);
      displaySnackbar(USER_SAVED_SUCCESS_MESSAGE, 'success');
      if (selectSearchValue.length > 0) {
        dispatch(setSearchUser(''));
      }
    } catch (error: any) {
      const message = error?.message ?? 'Something went wrong';
      displaySnackbar(ADD_USER_TO_GROUP_ERROR + message, 'error');
    }
  };

  const handleSaveExitSelectedUsers = async () => {
    if (selectSearchValue.length > 0) {
      dispatch(setSearchUser(''));
    }
    await handleSaveSelectedUsers();
    handleResetSelectedUsers();
    handleClose();
  };

  const handleCloseDialog = () => {
    handleResetSelectedUsers();
    dispatch(setSearchUser(''));
    handleClose();
  };

  const onSearchTextBoxChange = (searchValue: string) => {
    const searchStr = searchValue.trim();
    dispatch(setSearchUser(searchStr));
  };

  const onClearInput = () => {
    dispatch(setSearchUser(''));
    dispatch(resetUsersState());
  };

  const handleToggleSearch = () => setIsToggleSearch(!isToggleSearch);

  const handleDropdownChange = (event: LuiSelectItemEvent) => {
    const selectedValue: any = event.target.value;
    dispatch(setSearchKey(selectedValue));
    dispatch(setSearchUser(selectSearchValue || ''));
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: message,
        severity: severity
      })
    );
  };
  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Box className={classes.switchSection}>
          <Switch
            checked={isSelectedUsersShown}
            onChange={handleSelectedUsersShown}
            name="selected-users-swithcer"
            color="primary"
            size="small"
            disabled={!isSelectedUsersShown && selectedUsers.length === 0}
          />
          <Typography component={'div'}>{`${SHOW_SELECTED_USERS} ${
            selectedUsers.length > 0 ? `(${selectedUsers.length})` : ''
          }`}</Typography>
        </Box>
      )
    };
    const rightComponents = {
      component: (
        <Box className={classes.searchResetSection}>
          <Button
            name="save-exit-dialog-button"
            onClick={handleSaveExitSelectedUsers}
            variant="contained"
            color="primary"
            size="small"
            disabled={
              usersToShow.length === 0 || selectedUsers.length === 0 || selectedUsers.length > MAX_REQUEST_ITEMS
            }
          >
            {SAVE}
          </Button>
          <Button
            name="reset-button"
            onClick={handleResetSelectedUsers}
            variant="outlined"
            size="small"
            color="primary"
          >
            {RESET}
          </Button>

          {!isSelectedUsersShown && (
            <LuiDivider orientation="vertical" variant="middle" flexItem level={3} dotted={false} />
          )}

          <Box className={classes.searchSection}>
            <Box className={classes.searchComponent}>
              {isToggleSearch && !isSelectedUsersShown && (
                <>
                  <CustomSearch
                    value={selectSearchValue}
                    placeHolder={SEARCH_USER}
                    onClearInput={onClearInput}
                    onSearchChange={onSearchTextBoxChange}
                  />
                  <Box className={classes.conjuction}>
                    <Typography>{FOR}</Typography>
                  </Box>
                  <LuiSelect
                    inputLabel={''}
                    id="user-search-key"
                    selectedItem={searchKey}
                    itemData={SEARCH_FILTER_KEYS}
                    maxHeight={DROPDOWN_MAX_HEIGHT}
                    onSelectionChange={handleDropdownChange}
                    size="small"
                  />
                </>
              )}
            </Box>
            {!isSelectedUsersShown && (
              <IconButton
                edge="start"
                className={classes.searchButton}
                color="inherit"
                aria-label="close"
                onClick={handleToggleSearch}
                data-testid="search-button"
              >
                <SearchIcon />
              </IconButton>
            )}
          </Box>

          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };
  return (
    <Dialog fullScreen open={open} onClose={handleCloseDialog} className={classes.dialog}>
     
      <LuiPageHeader
        title={ADD_USERS}
        subtitle={`Add users to the user group: ${identifier}`}
        isCollapsable={false}
        removeBackButton={true}
        toolBarSlot={[
          {
            alwaysVisible: true,
            render: (
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseDialog}
                aria-label="close"
                data-testid="close-button"
              >
                <CloseIcon />
              </IconButton>
            )
          }
        ]}
      ></LuiPageHeader>
      <Box className={classes.userListWrap}>
        <CustomTablePagination
          columns={columns}
          data={usersToShow}
          loading={usersToShow.length === 0}
          data-testid="users-table"
          totalCount={paginationCount}
          rowsPerPageOptions={[5, 20, 50]}
          selectedRowsData={selectedUsers}
          labelRowsPerPage={ROWS_PER_PAGE_LABEL}
          onSelectedRowsDataChange={setSelectedUsers}
          withoutPagination={usersToShow.length === 0}
          onChangePageOptions={handleChangePageOptions}
          maxTableBodyHeight={height - 200}
          renderTopToolbar={extendedToolbar}
          currentPageNumber={isSelectedUsersShown ? pageDetailsSelectedUsers.pageNumber : selectedPagination.pageNumber}
          currentItemsPerPage={isSelectedUsersShown ? pageDetailsSelectedUsers.pageSize : selectedPagination.pageSize}
          additionalContent={
            <Box className={classes.alertMsg}>
              <AlertCircle className={classes.warningIcon} />
              <Typography variant="body1">{MAX_SELECTED_USERS_WARNING}</Typography>
            </Box>
          }
        />
      </Box>
      {snackBarMessage && snackBarMessage.showSnackbar && <SnackbarAlert {...snackBarMessage} />}
    </Dialog>
  );
}
